import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, EASE } from "@/components/Reveal";
import { resolveImg } from "@/lib/api";
import { useIntro } from "@/context/IntroContext";
import { ArrowRight } from "@phosphor-icons/react";

function youTubeId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url = "") {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function HeroMedia({ data }) {
  const videoRef = useRef(null);
  const { introActive } = useIntro();
  const video = (data?.background_video || "").trim();
  const yt = video && youTubeId(video);
  const vm = video && vimeoId(video);

  // Play the first 3s at a slow, cinematic rate, then freeze on the last frame.
  // Crucially, don't start until the splash/intro has finished revealing the hero —
  // otherwise the clip plays (and freezes) hidden behind the intro overlay and the
  // visitor only ever sees a static frame.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    const CAP = 3;          // play the first 3 seconds
    const RATE = 0.65;      // slow, cinematic
    let ended = false;      // finished the 3s (frozen on last frame)
    let armed = false;      // user scrolled away, ready to replay on return to top
    let started = false;
    const playOnce = () => {
      ended = false;
      try { el.currentTime = 0; } catch (e) { /* not seekable yet */ }
      el.playbackRate = RATE;
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    };
    const onTime = () => {
      if (ended) return;
      if (el.currentTime >= CAP) {
        ended = true;
        el.pause();  // freeze on the last frame — no transition
      }
    };
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      if (y > 300) armed = true;
      else if (y < 40 && armed && ended) { armed = false; playOnce(); }
    };
    const kick = () => { if (!started) { started = true; playOnce(); } };

    el.addEventListener("timeupdate", onTime);
    window.addEventListener("scroll", onScroll, { passive: true });

    if (!introActive) {
      kick();
      el.addEventListener("canplay", kick, { once: true });
      el.addEventListener("loadeddata", kick, { once: true });
    } else {
      // park on the first frame while the intro overlay covers the hero
      try { el.currentTime = 0; } catch (e) { /* noop */ }
      el.pause();
    }
    return () => {
      el.removeEventListener("timeupdate", onTime);
      window.removeEventListener("scroll", onScroll);
      el.removeEventListener("canplay", kick);
      el.removeEventListener("loadeddata", kick);
    };
  }, [video, introActive]);

  if (video && (yt || vm)) {
    const src = yt
      ? `https://www.youtube.com/embed/${yt}?autoplay=1&mute=1&loop=1&playlist=${yt}&controls=0&modestbranding=1&rel=0&playsinline=1`
      : `https://player.vimeo.com/video/${vm}?autoplay=1&muted=1&loop=1&background=1`;
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <iframe
          title="Nat Habitat hero"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full"
          style={{ width: "177.78vh", height: "56.25vw" }}
          src={src}
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>
    );
  }
  if (video) {
    return (
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        src={resolveImg(video)}
        muted
        playsInline
        preload="auto"
      />
    );
  }
  return (
    <img
      src={resolveImg(data?.background_image)}
      alt="Nat Habitat signature interior"
      className="h-full w-full object-cover"
    />
  );
}

export default function Hero({ data, onCtaPrimary, onCtaSecondary }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = data?.title_lines || ["Designing spaces with", "soul, detail", "and intention."];

  return (
    <section ref={ref} id="home" className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 pointer-events-none">
        <HeroMedia data={data} />
        {/* dark scrim that blends into the video for text legibility — stronger on the
            left & bottom (like the reference), fading cleanly so the right stays visible */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(68% 82% at 20% 58%, rgba(0,0,0,0.42), rgba(0,0,0,0) 62%)" }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-center pt-28 md:pt-32 pb-28">
        <div className="max-w-3xl md:ml-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-white/80 mb-7"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
          >
            {data?.label || "Interior Design Studio"}
          </motion.p>

          <h1
            className="font-serif font-light text-white text-4xl sm:text-5xl md:text-6xl lg:text-[4.75rem] leading-[1.15] tracking-tight"
            style={{ textShadow: "0 2px 28px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.4)" }}
          >
            <MaskedLines lines={lines} lineClassName="pb-2 pr-[0.35em]" start={0.15} stagger={0.13} bigWords={["soul", "detail", "intention"]} sizeByLine={{ 0: "0.88em" }} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: EASE }}
            className="mt-9 max-w-md text-white/85 text-base md:text-lg leading-relaxed"
            style={{ textShadow: "0 1px 14px rgba(0,0,0,0.55)" }}
          >
            {data?.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1, ease: EASE }}
            className="mt-11 flex flex-wrap items-center gap-8"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={onCtaPrimary}
              className="bg-foreground/85 backdrop-blur-sm text-background text-xs uppercase tracking-[0.2em] px-9 py-4 hover:bg-foreground transition-colors duration-500"
            >
              {data?.cta_primary || "View Projects"}
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={onCtaSecondary}
              className="group inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.2em]"
            >
              {data?.cta_secondary || "Start a Project"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-4 text-white/70"
      >
        <span className="h-px w-10 bg-white/40" />
        <span className="text-[10px] uppercase tracking-[0.25em]">Scroll to discover</span>
      </motion.div>
    </section>
  );
}
